import { useState, type ReactNode } from 'react';
import { Download } from 'lucide-react';
import {
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  MessageErreur,
  Pastille,
} from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import {
  heures,
  pourcentagePresence,
  urlRelevePresence,
  type LigneEmargementApprenant,
  type MonEmargementApprenant,
} from '../../lib/useEspaceApprenant';

/**
 * « Mon émargement » de l'apprenant (CDC §8.1) : sa synthèse, ses absences face
 * au seuil, tout son historique et ce qui l'attend.
 *
 * Tous les chiffres viennent de l'API, qui les lit comme la carte « Assiduité »
 * de l'équipe (`domain/assiduite.ts`) : un retard est une présence, une
 * demi-journée non pointée n'est pas une absence, et les heures suivies sont le
 * temps réellement passé en salle — celles que le certificat atteste.
 */

/** Lignes montrées d'abord, puis par tranche : un parcours de deux ans en compte des centaines. */
const TRANCHE = 20;

function horaires(ligne: LigneEmargementApprenant): string {
  return ligne.arriveeEffective && ligne.departEffectif
    ? `${ligne.arriveeEffective}–${ligne.departEffectif}`
    : `${ligne.heureDebut}–${ligne.heureFin}`;
}

/** « 3h30 », « 45 min », « — » tant que la ligne n'est pas pointée ou pour une absence. */
function duree(minutes: number | null): string {
  if (minutes === null || minutes === 0) return '—';
  if (minutes < 60) return `${minutes} min`;
  const reste = minutes % 60;
  return `${Math.floor(minutes / 60)}h${reste === 0 ? '' : String(reste).padStart(2, '0')}`;
}

/**
 * La signature, à part du statut : une présence pointée sans signature se dit
 * « non signée », une absence n'a rien à signer.
 */
function signature(ligne: LigneEmargementApprenant): string {
  if (!ligne.signeLe) {
    return ligne.statut === 'PRESENT' || ligne.statut === 'RETARD' ? 'Non signé' : '—';
  }
  const heure = new Date(ligne.signeLe).toLocaleTimeString('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  });
  return ligne.distance ? `Émargé dans la salle à ${heure}` : `Signé à ${heure}`;
}

function StatutLigne({ ligne }: { ligne: LigneEmargementApprenant }): JSX.Element {
  return (
    <span title={ligne.motifJustification ?? undefined}>
      <Pastille texte={ligne.qualificationLibelle} ton={ligne.qualificationTon} />
    </span>
  );
}

export function LienRelevePresence(): JSX.Element {
  return (
    <a
      href={urlRelevePresence()}
      className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
    >
      <Download className="h-4 w-4" aria-hidden="true" />
      Télécharger mon relevé
    </a>
  );
}

export function SyntheseMonEmargement({
  donnees,
}: {
  donnees: MonEmargementApprenant;
}): JSX.Element {
  const { synthese, cumul } = donnees;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Compteur
        libelle="Heures suivies"
        valeur={heures(synthese.heuresSuivies)}
        detail={`sur ${heures(synthese.heuresPrevues)} programmées`}
        ton="mint"
      />
      <Compteur
        libelle="Taux de présence"
        valeur={pourcentagePresence(synthese.tauxPresence)}
        detail={
          synthese.tauxPresence === null
            ? 'aucune demi-journée pointée'
            : 'depuis votre entrée en formation'
        }
        ton="blue"
      />
      <Compteur
        libelle="Absences justifiées"
        valeur={synthese.absencesJustifiees}
        detail="demi-journées"
        ton="navy"
      />
      <Compteur
        libelle="Absences non justifiées"
        valeur={cumul.injustifiees}
        detail={`seuil de la formation : ${cumul.seuil}`}
        ton={cumul.seuilDepasse ? 'red' : 'warm'}
      />
    </div>
  );
}

export function HistoriqueMonEmargement({
  lignes,
}: {
  lignes: LigneEmargementApprenant[];
}): JSX.Element {
  const [visibles, setVisibles] = useState(TRANCHE);
  const affichees = lignes.slice(0, visibles);

  return (
    <Carte titre="Mon historique de présence" sansBordure>
      {lignes.length === 0 ? (
        <p className="p-4 text-sm text-codialis-gray sm:p-6">
          Aucune demi-journée passée pour l’instant.
        </p>
      ) : (
        <>
          {/* Tablette et ordinateur : le tableau de la maquette. */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-codialis-gray">
                <tr>
                  {/* Cinq colonnes : la carte n'a que les deux tiers de l'écran. */}
                  {['Date', 'Séquence', 'Horaires', 'Statut', 'Signature'].map((titre) => (
                    <th key={titre} scope="col" className="px-4 py-3 text-left font-bold">
                      {titre}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {affichees.map((ligne) => (
                  <tr key={ligne.presenceId} className="transition hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="font-mono text-xs text-codialis-navy">{ligne.jourLisible}</div>
                      <div className="text-[11px] text-codialis-gray">{ligne.demiJourneeLibelle}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-codialis-navy">{ligne.seance}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="text-xs text-codialis-gray">{horaires(ligne)}</div>
                      <div className="font-mono text-[11px] text-codialis-navy">
                        {duree(ligne.minutesPresence)}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatutLigne ligne={ligne} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-codialis-gray">
                      {signature(ligne)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Téléphone : une carte par demi-journée, sans défilement horizontal. */}
          <ul className="divide-y divide-gray-100 md:hidden">
            {affichees.map((ligne) => (
              <li key={ligne.presenceId} className="flex items-start gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-codialis-navy">
                    {ligne.seance}
                  </div>
                  <div className="text-[11px] text-codialis-gray">
                    {ligne.jourLisible} · {ligne.demiJourneeLibelle} · {horaires(ligne)} ·{' '}
                    {duree(ligne.minutesPresence)}
                  </div>
                  {signature(ligne) !== '—' && (
                    <div className="text-[11px] text-codialis-gray">{signature(ligne)}</div>
                  )}
                </div>
                <StatutLigne ligne={ligne} />
              </li>
            ))}
          </ul>

          {visibles < lignes.length && (
            <div className="border-t border-gray-100 p-4 sm:px-6">
              <BoutonSecondaire onClick={() => setVisibles((nombre) => nombre + TRANCHE)}>
                Afficher les plus anciennes ({lignes.length - visibles} restantes)
              </BoutonSecondaire>
            </div>
          )}
        </>
      )}
    </Carte>
  );
}

export function ProgressionMonEmargement({
  donnees,
  action,
}: {
  donnees: MonEmargementApprenant;
  /** « Justifier une absence », sous le rappel du seuil comme dans la maquette. */
  action?: ReactNode;
}): JSX.Element {
  const { synthese, cumul } = donnees;
  const progression =
    synthese.heuresPrevues > 0
      ? Math.min(100, Math.round((synthese.heuresSuivies / synthese.heuresPrevues) * 100))
      : 0;

  return (
    <Carte titre="Ma progression horaire" sansBordure>
      <div className="space-y-3 p-4 sm:p-6">
        <div>
          <div className="mb-1.5 flex justify-between gap-2 text-xs text-codialis-gray">
            <span>Heures suivies</span>
            <span className="font-bold text-codialis-navy">
              {heures(synthese.heuresSuivies)} / {heures(synthese.heuresPrevues)}
            </span>
          </div>
          <div className="progress-track h-2 overflow-hidden rounded-full">
            <div className="progress-bar h-full" style={{ width: `${progression}%` }} />
          </div>
        </div>
        <p className="text-[11px] text-codialis-gray">
          Temps réellement passé en formation sur les demi-journées pointées : une arrivée
          tardive ou un départ anticipé comptent pour leur durée.
          {synthese.attendues > 0
            ? ` ${synthese.attendues} demi-journée${synthese.attendues > 1 ? 's' : ''} reste${synthese.attendues > 1 ? 'nt' : ''} à pointer.`
            : ''}
        </p>
        <p
          className={`rounded-xl px-3 py-2 text-[11px] ${
            cumul.seuilDepasse ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900'
          }`}
        >
          {cumul.seuilDepasse
            ? `Vous avez atteint le seuil de ${cumul.seuil} demi-journées d’absence non justifiées : un courrier d’avertissement vous est adressé, puis à chaque nouveau palier. Vous en comptez ${cumul.injustifiees}.`
            : `À ${cumul.seuil} demi-journées d’absence non justifiées, un courrier d’avertissement vous est adressé. Vous en comptez ${cumul.injustifiees}.`}
        </p>
        {action}
      </div>
    </Carte>
  );
}

export function ProchainesMonEmargement({
  lignes,
}: {
  lignes: LigneEmargementApprenant[];
}): JSX.Element {
  return (
    <Carte titre="Mes prochaines demi-journées" sansBordure>
      {lignes.length === 0 ? (
        <p className="p-4 text-sm text-codialis-gray sm:p-6">
          Aucune demi-journée n’est encore programmée.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {lignes.map((ligne) => (
            <li key={ligne.presenceId} className="flex items-center gap-3 px-4 py-3 sm:px-6">
              <span className="shrink-0 font-mono text-[11px] text-codialis-gray">
                {ligne.jourLisible.slice(0, 5)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-codialis-navy">{ligne.seance}</div>
                <div className="text-[11px] text-codialis-gray">
                  {ligne.demiJourneeLibelle} · {ligne.heureDebut}–{ligne.heureFin}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Carte>
  );
}

/** Chargement et erreur, dits comme ailleurs dans l'espace. */
export function EtatMonEmargement({
  isLoading,
  error,
}: {
  isLoading: boolean;
  error: unknown;
}): JSX.Element | null {
  if (isLoading) return <Chargement libelle="Lecture de votre émargement…" />;
  if (error) {
    return (
      <MessageErreur
        message={
          error instanceof ErreurApi ? error.message : 'Votre émargement n’a pas pu être chargé.'
        }
      />
    );
  }
  return null;
}
